// src/modules/care-gaps-2/pages/CareGapsPage.jsx
import * as React from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Paper,
  Chip,
  Divider,
  Autocomplete,
  Button,
  Collapse,
  Tooltip,
  Badge,
  Snackbar,
  Alert,
  Slide,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";

// Icons
import FilterListIcon from "@mui/icons-material/FilterList";
import BarChartIcon from "@mui/icons-material/BarChart";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import CareGapList from "../components/CareGapList";
import CareGapDetailDrawer from "./CareGapDetailDrawer";
import ScheduleDialog from "../dialogs/ScheduleDialog";
import EvidenceDialog from "../dialogs/EvidenceDialog";
import OutreachDialog from "../dialogs/OutreachDialog";
import CareGapActionsDialog from "../dialogs/CareGapActionsDialog";
import { useObservationsModal } from "../../../context/ObservationsModalContext/ObservationsModalContext";

import {
  WORKFLOW_STATUS_OPTIONS,
  CARE_GAP_STATUS_OPTIONS,
  CARE_GAP_TYPE_OPTIONS,
  OUTREACH_RESULT_TO_WORKFLOW_CODE,
} from "../common/constants";

import { CareGapContext } from "../../../context/CareGapContext/CareGapContext";
import { ObservationContext } from "../../../context/ObservationContext/ObservationContext";
import useAuthUser from "../../../hooks/useAuthUser";

// Dashboards
import CareGapTypeDashboard from "../components/CareGapTypeDashboard";
import CareGapStatusPieDashboard from "../components/CareGapStatusPieDashboard";

const SCHEDULE_WORKFLOW_STATUS_ID = 4; // Scheduled

export default function CareGapsPage() {
  const {
    careGaps,
    totalCount,
    filters,
    loading,
    error,
    selectCareGap,
    addEvidence,
    addOutreach,
    updateCareGap,
    workflowStatuses,

    applyFilters,
    goToPage,
    changePageSize,
  } = React.useContext(CareGapContext);

  const { addObservation } = React.useContext(ObservationContext);

  const user = useAuthUser();
  // employeeId como número. Cada caller lo convierte al tipo que espera el
  // endpoint correspondiente (ver CareGapContext: outreach/evidence usan
  // string, updateCareGap/audit usan number).
  const rawEmployeeId = user?.employeeId ?? user?.employee ?? null;
  const employeeId =
    rawEmployeeId !== null && rawEmployeeId !== ""
      ? Number(rawEmployeeId) || null
      : null;

  const [selected, setSelected] = React.useState(null);
  const [openSchedule, setOpenSchedule] = React.useState(false);
  const [openEvidence, setOpenEvidence] = React.useState(false);
  const [openOutreach, setOpenOutreach] = React.useState(false);
  const [openCareGapActions, setOpenCareGapActions] = React.useState(false);

  // Snackbar para feedback de acciones (registrar evidencia, outreach, etc.)
  const [snackbar, setSnackbar] = React.useState(null); // { severity, message }
  const showSnackbar = (severity, message) => setSnackbar({ severity, message });
  const closeSnackbar = () => setSnackbar(null);

  // Resultado del context tiene shape { ok: bool, message: string }.
  const handleResult = (result, fallbackOk, fallbackErr) => {
    if (!result) return;
    if (result.ok) {
      showSnackbar("success", result.message || fallbackOk);
    } else {
      showSnackbar("error", result.message || fallbackErr);
    }
  };
  const { showObservationsModal } = useObservationsModal();

  // UI toggle states
  const [showCharts, setShowCharts] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);

  // draft filters
  const [draftFilters, setDraftFilters] = React.useState(filters);

  // Autocomplete selected
  const [selectedType, setSelectedType] = React.useState(null);

  React.useEffect(() => {
    setDraftFilters(filters);
  }, [filters]);

  const { page, pageSize } = filters;
  const { search, workflowStatusCode, sourceStatusCode } = draftFilters;

  // ---------- draft handlers ----------
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setDraftFilters((prev) => ({ ...prev, search: value, page: 1 }));
  };

  const handleWorkflowStatusChange = (e) => {
    const value = e.target.value;
    setDraftFilters((prev) => ({
      ...prev,
      workflowStatusCode: value || "",
      page: 1,
    }));
  };

  const handleCareGapStatusChange = (e) => {
    const value = e.target.value;
    setDraftFilters((prev) => ({
      ...prev,
      sourceStatusCode: value || "",
      page: 1,
    }));
  };

  const handleTypeChange = (_event, option) => {
    setSelectedType(option);
    setDraftFilters((prev) => ({
      ...prev,
      // si el endpoint soporta id también, bien; si no, no pasa nada
      careGapTypeId: option?.id || null,
      // ✅ ESTE es el filtro por descripción (displayName)
      careGapNameSearch: option?.displayName || "",
      page: 1,
    }));
  };

  // ✅ Click en barra del dashboard -> filtra por DESCRIPCIÓN (careGapNameSearch)
  const handleSelectMeasureFromChart = async (row) => {
    if (!row) return;

    const desc =
      row?.careGapTypeDescription ??
      row?.description ??
      row?.displayName ??
      row?.label ??
      row?.careGapType ??
      "";

    const next = {
      ...draftFilters,
      careGapTypeId: null,         // ✅ NO dependemos del id
      careGapNameSearch: desc,     // ✅ SOLO descripción
      page: 1,
      // opcional: si tu "search" general se mete, descomenta:
      // search: "",
    };

    setSelectedType(null); // (porque ahora no depende del autocomplete)
    setDraftFilters(next);

    // 🔥 debug mínimo
    console.log("[ChartFilter] Applying careGapNameSearch:", desc);

    await applyFilters(next);
  };


  const handleApplyFilters = async () => {
    await applyFilters(draftFilters);
  };

  const handleClearFilters = async () => {
    setSelectedType(null);

    const cleared = {
      ...filters,
      search: "",
      workflowStatusCode: "",
      sourceStatusCode: "",
      careGapTypeId: null,
      careGapNameSearch: "",
      page: 1,
    };

    setDraftFilters(cleared);
    await applyFilters(cleared);
  };

  // ---------- pagination ----------
  const totalPages = React.useMemo(() => {
    const size = pageSize || 1;
    if (totalCount <= 0) return 1;
    return Math.max(1, Math.ceil(totalCount / size));
  }, [totalCount, pageSize]);

  const handlePageChange = async (_event, newPage) => {
    await goToPage(newPage);
  };

  const handlePageSizeChange = async (e) => {
    await changePageSize(e.target.value);
  };

  const startIndex = totalCount === 0 ? 0 : (page - 1) * (pageSize || 1) + 1;
  const endIndex = Math.min(page * (pageSize || 1), totalCount);

  // ---------- list actions ----------
  const handleOpenDetail = (item) => {
    setSelected(item);
    selectCareGap(item.id);
  };

  const handleSchedule = (item) => {
    setSelected(item);
    setOpenSchedule(true);
  };

  const handleSubmitEvidence = (item) => {
    setSelected(item);
    setOpenEvidence(true);
  };

  const handleLogOutreach = (item) => {
    setSelected(item);
    setOpenOutreach(true);
  };

  const handleOpenObservations = (item) => {
    showObservationsModal(item?.cipId, 2, item?.patientFullName);
  };

  const handleCareGapActions = (item) => {
    setSelected(item);
    setOpenCareGapActions(true);
  };

  // ---------- dialog confirms ----------
  const handleConfirmEvidence = async ({ codes, note, item }) => {
    if (!item) return;

    const result = await addEvidence({
      careGapId: item.id,
      legacyFileId: codes || "",
      fileName: note || item.careGapName,
      filePath: "",
      uploadedBy: employeeId || null,
    });
    handleResult(result, "Evidence submitted successfully.", "Could not submit evidence.");
  };

  const handleConfirmOutreach = async ({ channel, result, nextAction, item }) => {
    if (!item) return;

    const apiResult = await addOutreach({
      careGapId: item.id,
      contactMethod: channel,
      attemptDate: new Date().toISOString(),
      employee: employeeId || null,
      result,
      notes: nextAction,
    });

    if (!apiResult?.ok) {
      handleResult(apiResult, "", "Could not log outreach.");
      return;
    }

    // Auto-update del workflow status según el `result` seleccionado.
    // Mapeo definido en common/constants.js (OUTREACH_RESULT_TO_WORKFLOW_CODE).
    const targetCode = OUTREACH_RESULT_TO_WORKFLOW_CODE[result];
    const currentCode = String(item.workflowStatusCode || "").toUpperCase();
    if (targetCode && targetCode !== currentCode) {
      const target = (workflowStatuses || []).find(
        (ws) => String(ws.code || "").toUpperCase() === targetCode
      );
      if (target) {
        const updateRes = await updateCareGap({
          careGapId: item.id,
          newWorkflowStatusId: target.workflowStatusId ?? target.id,
          newAssignedEmployeeId: item.assignedEmployeeId || employeeId || 0,
          newPriority: item.priority ?? 0,
          newServiceDateOfClosure: null,
          note: `Auto status update from outreach: ${result}`,
        });
        if (updateRes?.ok) {
          showSnackbar(
            "success",
            `Outreach logged. Status → ${target.display || target.code}`
          );
          return;
        }
        // El outreach quedó OK pero falló el cambio de status — avisamos.
        showSnackbar(
          "warning",
          "Outreach logged, but workflow status could not be updated."
        );
        return;
      }
    }

    showSnackbar("success", "Outreach logged successfully.");
  };

  const handleConfirmSchedule = async ({ date, location, item }) => {
    if (!item) return;

    const isoDate =
      date && !Number.isNaN(new Date(date).getTime())
        ? new Date(date).toISOString()
        : null;

    const updateResult = await updateCareGap({
      careGapId: item.id,
      newWorkflowStatusId: SCHEDULE_WORKFLOW_STATUS_ID,
      newAssignedEmployeeId: item.assignedEmployeeId || 0,
      newPriority: item.priority ?? 0,
      newServiceDateOfClosure: isoDate,
      note: location || "Scheduled from care gaps UI",
    });

    if (!updateResult?.ok) {
      handleResult(updateResult, "", "Could not schedule care gap.");
      return;
    }

    const englishDate = date || new Date().toISOString().substring(0, 10);
    const observationText = `Appointment scheduled for ${englishDate}${
      location ? ` at ${location}` : ""
    } for care gap "${item.careGapName}".`;

    try {
      await addObservation({
        cipId: item.cipId,
        note: observationText,
        source: "CARE_GAP_SCHEDULE",
      });
    } catch (err) {
      // No bloquear: el care gap ya quedó agendado, solo se loggea si falla la observación.
      console.warn("Schedule succeeded but observation failed:", err);
    }

    showSnackbar("success", "Appointment scheduled successfully.");
  };

  const handleConfirmCareGapActions = async ({ workflowStatusId, note, item }) => {
    if (!item) return;

    const result = await updateCareGap({
      careGapId: item.id,
      newWorkflowStatusId: workflowStatusId,
      newAssignedEmployeeId: employeeId || 0,
      newPriority: item.priority ?? 0,
      newServiceDateOfClosure: new Date().toISOString(),
      note: note || "",
    });
    handleResult(result, "Care gap updated successfully.", "Could not update care gap.");
  };

  // ---------- quick counts ----------
  const counts = React.useMemo(
    () => ({
      total: careGaps.length,
      open: careGaps.filter((x) => x.sourceStatusCode === "OPEN").length,
      submitted: careGaps.filter((x) => x.sourceStatusCode === "SUBMITTED").length,
    }),
    [careGaps]
  );

  const hasError = !!error;
  const isEmpty = !loading && !hasError && (careGaps?.length || 0) === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2.5}>
        {/* HEADER */}
        <Paper sx={{ p: 2.5, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" fontWeight={900}>
                Care Gaps
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Worklist of care gaps with filters by patient, workflow status, care gap
                status and measure type.
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              {/* Toggle Buttons */}
              <Tooltip title={showCharts ? "Hide Status Chart" : "Show Status Chart"}>
                <Button
                  variant={showCharts ? "contained" : "outlined"}
                  size="small"
                  startIcon={<BarChartIcon />}
                  endIcon={showCharts ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={() => setShowCharts(!showCharts)}
                  color="primary"
                >
                  Status Chart
                </Button>
              </Tooltip>

              <Tooltip title={showFilters ? "Hide Filters" : "Show Filters"}>
                <Badge
                  color="info"
                  variant="dot"
                  invisible={!draftFilters.search && !draftFilters.workflowStatusCode && !draftFilters.sourceStatusCode && !draftFilters.careGapNameSearch}
                >
                  <Button
                    variant={showFilters ? "contained" : "outlined"}
                    size="small"
                    startIcon={<FilterListIcon />}
                    endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    onClick={() => setShowFilters(!showFilters)}
                    color="secondary"
                  >
                    Filters
                  </Button>
                </Badge>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />

              {/* Counts */}
              <Chip label={`Total (page): ${counts.total}`} size="small" />
              <Chip label={`Open: ${counts.open}`} color="warning" size="small" />
              <Chip label={`Submitted: ${counts.submitted}`} color="secondary" size="small" />
            </Stack>
          </Stack>
        </Paper>

        {draftFilters?.careGapNameSearch ? (
          <Chip
            color="info"
            label={`Chart filter: ${draftFilters.careGapNameSearch}`}
            onDelete={async () => {
              const cleared = { ...draftFilters, careGapNameSearch: "", careGapTypeId: null, page: 1 };
              setDraftFilters(cleared);
              await applyFilters(cleared);
            }}
          />
        ) : null}

        {/* DASHBOARD TYPE - Always visible (click -> filtra por descripción) */}
        <CareGapTypeDashboard onSelectMeasure={handleSelectMeasureFromChart} />

        {/* HEDIS STATUS CHART - Collapsible */}
        <Collapse in={showCharts} timeout="auto" unmountOnExit>
          <CareGapStatusPieDashboard items={careGaps} />
        </Collapse>

        {/* FILTERS & PAGINATION */}
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={2}>
            {/* Collapsible Filters */}
            <Collapse in={showFilters} timeout="auto" unmountOnExit>
              <Stack spacing={2} sx={{ mb: 2 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                  <TextField
                    label="Search (patient / gap / PCP / CIP)"
                    value={search || ""}
                    onChange={handleSearchChange}
                    fullWidth
                  />

                  <TextField
                    label="Workflow status"
                    select
                    value={workflowStatusCode || ""}
                    onChange={handleWorkflowStatusChange}
                    sx={{ minWidth: 220 }}
                  >
                    {WORKFLOW_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.code ?? "ALL"} value={opt.code}>
                        {opt.display}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    label="Care gap status"
                    select
                    value={sourceStatusCode || ""}
                    onChange={handleCareGapStatusChange}
                    sx={{ minWidth: 200 }}
                  >
                    {CARE_GAP_STATUS_OPTIONS.map((opt) => (
                      <MenuItem key={opt.code ?? "ALL"} value={opt.code}>
                        {opt.display}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", md: "center" }}
                >
                  <Autocomplete
                    sx={{ minWidth: 320 }}
                    options={CARE_GAP_TYPE_OPTIONS}
                    getOptionLabel={(opt) => opt.displayName || ""}
                    value={selectedType}
                    onChange={handleTypeChange}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Care gap type"
                        placeholder="Type to search by measure"
                      />
                    )}
                    isOptionEqualToValue={(opt, val) => opt.id === val?.id}
                  />

                  <Box sx={{ flexGrow: 1 }} />

                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleApplyFilters} disabled={loading}>
                      Apply filters
                    </Button>

                    <Button variant="outlined" onClick={handleClearFilters} disabled={loading}>
                      Clear
                    </Button>
                  </Stack>
                </Stack>

                <Divider />
              </Stack>
            </Collapse>

            {/* TOP PAGINATION - Always visible */}
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Typography variant="body2" color="text.secondary">
                Showing <b>{startIndex}-{endIndex}</b> of <b>{totalCount}</b> care gaps
                (page {page} of {totalPages})
              </Typography>

              <Box sx={{ flexGrow: 1 }} />

              <TextField
                select
                label="Rows per page"
                value={pageSize || 50}
                onChange={handlePageSizeChange}
                size="small"
                sx={{ width: 160 }}
              >
                {[10, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </TextField>

              <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
            </Stack>
          </Stack>
        </Paper>

        {/* STATUS */}
        {loading && (
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Loading care gaps...
            </Typography>
          </Paper>
        )}

        {hasError && (
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="error">
              Error loading care gaps: {error.message || "Unknown error"}
            </Typography>
          </Paper>
        )}

        {isEmpty && (
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No care gaps found with current filters.
            </Typography>
          </Paper>
        )}

        {/* LIST */}
        {!loading && !hasError && (
          <CareGapList
            items={careGaps}
            onOpenDetail={handleOpenDetail}
            onSchedule={handleSchedule}
            onSubmitEvidence={handleSubmitEvidence}
            onLogOutreach={handleLogOutreach}
            onCareGapActions={handleCareGapActions}
            onOpenObservations={handleOpenObservations}
          />
        )}

        {/* BOTTOM PAGINATION */}
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            alignItems={{ xs: "flex-start", md: "center" }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing <b>{startIndex}-{endIndex}</b> of <b>{totalCount}</b> care gaps
              (page {page} of {totalPages})
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <TextField
              select
              label="Rows per page"
              value={pageSize || 50}
              onChange={handlePageSizeChange}
              size="small"
              sx={{ width: 160 }}
            >
              {[10, 25, 50, 100].map((size) => (
                <MenuItem key={size} value={size}>
                  {size}
                </MenuItem>
              ))}
            </TextField>

            <Pagination count={totalPages} page={page} onChange={handlePageChange} color="primary" />
          </Stack>
        </Paper>
      </Stack>

      {/* DETAIL */}
      <CareGapDetailDrawer
        open={!!selected}
        onClose={() => setSelected(null)}
        item={selected}
        onSchedule={handleSchedule}
        onSubmitEvidence={handleSubmitEvidence}
        onLogOutreach={handleLogOutreach}
        onCareGapActions={handleCareGapActions}
        onShowObservations={handleOpenObservations}
      />

      {/* DIALOGS */}
      <ScheduleDialog
        open={openSchedule}
        onClose={() => setOpenSchedule(false)}
        item={selected}
        onConfirm={handleConfirmSchedule}
      />

      <EvidenceDialog
        open={openEvidence}
        onClose={() => setOpenEvidence(false)}
        item={selected}
        onConfirm={handleConfirmEvidence}
      />

      <OutreachDialog
        open={openOutreach}
        onClose={() => setOpenOutreach(false)}
        item={selected}
        onConfirm={handleConfirmOutreach}
      />

      <CareGapActionsDialog
        open={openCareGapActions}
        onClose={() => setOpenCareGapActions(false)}
        item={selected}
        onConfirm={handleConfirmCareGapActions}
        workflowStatuses={workflowStatuses}
      />

      {/* Snackbar de feedback para evidence / outreach / schedule / actions */}
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        TransitionComponent={(props) => <Slide {...props} direction="left" />}
      >
        {snackbar && (
          <Alert
            onClose={closeSnackbar}
            severity={snackbar.severity}
            variant="filled"
            elevation={6}
            sx={{
              minWidth: 320,
              borderRadius: 2.5,
              fontWeight: 600,
              fontSize: "0.92rem",
              alignItems: "center",
              boxShadow:
                snackbar.severity === "success"
                  ? "0 8px 24px rgba(22, 163, 74, 0.35)"
                  : "0 8px 24px rgba(220, 38, 38, 0.35)",
              "& .MuiAlert-icon": { fontSize: 24 },
              ...(snackbar.severity === "success" && {
                background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
              }),
              ...(snackbar.severity === "error" && {
                background: "linear-gradient(135deg, #EF4444 0%, #DC2626 100%)",
              }),
            }}
          >
            {snackbar.message}
          </Alert>
        )}
      </Snackbar>

    </Container>
  );
}
